/**
 * TIME_RECOVERY: the owner's switch for bounded purchases into PRIME/PULSE
 * positions stuck for two weeks, and what each cycle has bought so far.
 *
 * Off unless the owner turned it on. The rules are the server's; the page
 * names them and shows the Recovery account of every cycle that used it.
 */
import { useMutation } from "@tanstack/react-query";

import { Button } from "@/design-system/Button";
import { Panel } from "@/design-system/Panel";
import { StatusBadge } from "@/design-system/StatusBadge";
import { setClientTimeRecovery } from "@/shared/api";
import type { ClientAccountResponse } from "@/shared/api";
import { money } from "@/shared/money/money";

function day(iso: string): string {
  return new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
}

function moment(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

type Watch = NonNullable<ClientAccountResponse["time_recovery"]>["watch"];
type Used = NonNullable<NonNullable<ClientAccountResponse["time_recovery"]>["cycles"]>;

function times(count: number): string {
  const tens = count % 100;
  const last = count % 10;
  const word = tens >= 11 && tens <= 14 ? "раз" : last >= 2 && last <= 4 ? "раза" : "раз";
  return `${String(count)} ${word}`;
}

/** The coin of a cycle the watch no longer lists: its id starts with it. */
function coinOf(cycleId: string): string {
  return cycleId.split("-")[0] ?? cycleId;
}

/**
 * One row a position: what it is, where it stands, what it bought. Two lists
 * keyed by raw cycle ids read as a log, not as a product (owner, 2026-10-30).
 */
function RecoveryTable({ watch, used, now }: {
  readonly watch: NonNullable<Watch>; readonly used: Used; readonly now: number;
}): React.JSX.Element {
  const bought = new Map(used.map((cycle) => [cycle.cycle_id, cycle]));
  const rows = [
    ...watch.map((item) => ({ id: item.cycle_id, coin: item.symbol, pool: item.pool, watch: item })),
    ...used
      .filter((cycle) => !watch.some((item) => item.cycle_id === cycle.cycle_id))
      .map((cycle) => ({ id: cycle.cycle_id, coin: coinOf(cycle.cycle_id), pool: null, watch: null })),
  ];
  if (rows.length === 0) {
    return (
      <p className="text-helper-sm" data-testid="time-recovery-empty">
        Позиций с полностью исполненной сеткой нет — докупать некого.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-border-subtle" data-testid="time-recovery-cycles">
      <li className="hidden py-2 text-label-caps text-text-muted md:grid md:grid-cols-4 md:gap-3">
        <span>Позиция</span>
        <span>Состояние</span>
        <span>Докуплено</span>
        <span>Последняя докупка</span>
      </li>
      {rows.map((row) => {
        const cycle = bought.get(row.id);
        const state = cycle?.pending === true
          ? "ждёт подтверждения"
          : row.watch === null
            ? "докупки были"
            : Date.parse(row.watch.eligible_at) > now
              ? `подойдёт ${day(row.watch.eligible_at)}`
              : "ждёт следующего уровня";
        return (
          <li key={row.id} className="grid grid-cols-2 gap-x-3 gap-y-1 py-2 md:grid-cols-4" data-testid="time-recovery-row">
            <span className="font-medium text-text-primary">
              {row.coin.replace(/USDT$/, "")}
              {row.pool !== null && <span className="ml-1 text-helper-sm font-normal text-text-muted">{row.pool}</span>}
            </span>
            <span>{state}</span>
            <span className="tabular-nums text-text-primary">
              {cycle === undefined
                ? "—"
                : `${money(cycle.spent)} из ${money(cycle.budget)} USDT · ${times(cycle.purchases)}`}
            </span>
            <span className="tabular-nums">
              {cycle?.last_purchase_at == null ? "—" : moment(cycle.last_purchase_at)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function TimeRecoveryPanel({ data, onSaved }: {
  readonly data: ClientAccountResponse; readonly onSaved: () => Promise<void>;
}): React.JSX.Element {
  const recovery = data.time_recovery ?? { enabled: false, cycles: [] };
  const toggle = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error, response } = await setClientTimeRecovery({ body: { enabled } });
      if (error !== undefined || response?.ok !== true) throw new Error("switch refused");
    },
    onSuccess: onSaved,
  });
  const used = (recovery.cycles ?? []).filter((cycle) => cycle.purchases > 0 || cycle.pending);
  const watch = recovery.watch ?? [];
  const filling = recovery.ladder_open ?? 0;
  const now = Date.now();
  return (
    <Panel
      title="Докупка зависших позиций"
      testId="time-recovery"
      trailing={
        <StatusBadge tone={recovery.enabled ? "active" : "muted"}>
          {recovery.enabled ? "Включена" : "Не включена"}
        </StatusBadge>
      }
    >
      <div className="flex flex-col gap-3 text-body-main text-text-secondary">
        <p className="text-text-primary">Позиции, которая долго стоит в минусе с исполненной сеткой, бот ставит вторую такую же сетку от текущей цены, чтобы она быстрее вышла в плюс.</p>
        <details className="text-helper-sm">
          <summary className="cursor-pointer select-none rounded-row focus-visible:outline-2 focus-visible:outline-focus-ring">Как это работает</summary>
          <p className="mt-2">
            Если позиция открыта 14 дней и вся её сетка уже исполнена, бот строит вторую сетку той
            же формы от текущей цены до той же глубины: первый ордер покупается сразу, следующие —
            когда цена до них дойдёт. Всего не больше маржи слота. Каждая покупка проходит проверку
            риска счёта. Пока цена не дошла до уровня, деньги под него свободны и работают в других
            сделках.
          </p>
        </details>
        {!data.can_manage && (
          <p className="text-helper-sm text-text-muted" data-testid="time-recovery-owner-only">Включает и выключает владелец счёта под своим логином.</p>
        )}
        {data.can_manage && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              disabled={toggle.isPending}
              onClick={() => { toggle.mutate(!recovery.enabled); }}
            >
              {recovery.enabled ? "Выключить" : "Включить"}
            </Button>
            {toggle.isError && (
              <p role="alert" className="text-status-warning">Не сохранено. Обновите страницу и попробуйте ещё раз.</p>
            )}
          </div>
        )}
        <div className="flex flex-col gap-1" data-testid="time-recovery-watch">
          <RecoveryTable watch={watch} used={used} now={now} />
          {filling > 0 && (
            <p className="text-helper-sm text-text-muted">
              Ещё у {filling} позиций сетка исполняется — докупка им не нужна.
            </p>
          )}
        </div>
      </div>
    </Panel>
  );
}
